import React from 'react';
import { Badge, ScrollArea, TruncatedText } from '@ais-ui/ui-core';
import { PiBoat } from 'react-icons/pi';
import {
  BaseInformationView,
  AdvancedVesselInformationView,
  ExtendedVesselIntegrityInformation,
  ExtendedVesselRegisterInformation,
  TabsDetailsView,
} from '../Targets/ShipDetailsTabs';
import { TargetTab } from '../Targets/targetTypes';
import { ShipInfo } from '@ais-ui/websockets';
import {
  VesselAdvancedIntegrity,
  VesselAdvancedVessel,
  VesselAdvancedRegister,
} from '@ais-ui/api-spec/shipdb';
import { PopupVesselPermissions } from './popupVesselTypes';

interface VesselDetailsSidebarProps {
  shipInfo: ShipInfo;
  tab: TargetTab;
  onTabChange: (tab: TargetTab) => void;
  permissions?: PopupVesselPermissions;
  advancedVessel?: VesselAdvancedVessel;
  advancedIntegrity?: VesselAdvancedIntegrity;
  advancedRegister?: VesselAdvancedRegister;
}

export const VesselDetailsSidebar: React.FC<VesselDetailsSidebarProps> = ({
  shipInfo,
  tab,
  onTabChange,
  permissions,
  advancedVessel,
  advancedIntegrity,
  advancedRegister,
}) => {
  return (
    <div className="w-[400px] border-l flex-shrink-0 flex flex-col">
      {/* Header similar to TargetCardContent */}
      <div className="p-4 border-b">
        <div className="flex flex-col space-y-2">
          <div className="flex justify-between items-start">
            <div>
              <h2 className="text-lg font-medium">
                <TruncatedText
                  text={shipInfo.name || 'Unknown'}
                  maxWidth="250px"
                />
              </h2>
              <p className="text-sm text-muted-foreground">{shipInfo.mmsi}</p>
            </div>
            {/* Tabs */}
            <TabsDetailsView
              tab={tab}
              onTabChange={onTabChange}
              imo={shipInfo.imo}
              permissions={{
                mdmAdvancedVesselInfo: permissions?.mdmAdvancedVesselInfo,
                mdmRegister: permissions?.mdmRegister,
                mdmIntegrity: permissions?.mdmIntegrity,
                nextport: false, // Not supported in popup
              }}
              hideNextPort={true}
            />
          </div>
          {shipInfo.shipType && (
            <Badge className="text-xs w-fit space-x-2" variant="secondary">
              <PiBoat className="h-3 w-3" />
              <span>{shipInfo.shipType}</span>
            </Badge>
          )}
        </div>
      </div>
      {/* Scrollable content */}
      <ScrollArea className="flex-1">
        <div className="p-4">
          {tab === 'base' && (
            <BaseInformationView shipInfo={shipInfo} showLastPortCall={false} />
          )}
          {tab === 'advancedVessel' &&
            (advancedVessel ? (
              <AdvancedVesselInformationView advancedVessel={advancedVessel} />
            ) : (
              <div className="text-sm text-muted-foreground">
                No advanced vessel information available.
              </div>
            ))}
          {tab === 'integrity' &&
            (advancedIntegrity ? (
              <ExtendedVesselIntegrityInformation
                extendedVesselInfo={{
                  advancedIntegrity: advancedIntegrity,
                }}
              />
            ) : (
              <div className="text-sm text-muted-foreground">
                No integrity information available.
              </div>
            ))}
          {tab === 'register' &&
            (advancedRegister ? (
              <ExtendedVesselRegisterInformation
                extendedVesselInfo={{
                  advancedRegister: advancedRegister,
                }}
              />
            ) : (
              <div className="text-sm text-muted-foreground">
                No register information available.
              </div>
            ))}
        </div>
      </ScrollArea>
    </div>
  );
};
